import {
    CustomTimeframeValue,
    isIsoDateString,
    isUnixEpochSeconds,
    resolveTimeframeComponents,
    resolveTimeframeValue
} from '@squaredup/timeframes';
import { Day, parseJSON } from 'date-fns';
import { z, ZodSchema } from 'zod';
import { toCustomTimeframeValue } from './timeframeFormUtils';

export const unitOptionsSchema = z.enum(['minute', 'hour', 'day', 'week', 'month', 'quarter', 'year']).nullable();

export const unitOptions = [
    { label: 'minute', value: 'minute' },
    { label: 'hour', value: 'hour' },
    { label: 'day', value: 'day' },
    { label: 'week', value: 'week' },
    { label: 'month', value: 'month' },
    { label: 'quarter', value: 'quarter' },
    { label: 'year', value: 'year' }
] as const;

export type TimeframeFieldType = z.infer<typeof typeOptionsSchema>;

export const modifierOptionsSchema = z.enum(['exact', 'minus', 'plus']).nullable();

export const modifierOptions = [
    { label: 'exactly', value: 'exact' },
    { label: 'minus', value: 'minus' },
    { label: 'plus', value: 'plus' }
] as const;

export type TimeframeFieldModifier = z.infer<typeof modifierOptionsSchema>;

export const typeOptionsSchema = z.enum(['fixed', 'now', 'startPrevious', 'endPrevious', 'start', 'end']).nullable();

export const typeOptions = [
    {
        name: 'relative',
        options: [
            { value: 'now', label: 'Now' },
            { value: 'start', label: 'Start of this' },
            { value: 'end', label: 'End of this' },
            { value: 'startPrevious', label: 'Start of last' },
            { value: 'endPrevious', label: 'End of last' }
        ]
    },
    {
        name: 'custom',
        options: [
            {
                value: 'fixed',
                label: 'Specific'
            }
        ]
    }
] as const;

export type TimeframeFieldUnit = z.infer<typeof unitOptionsSchema>;

export type RelativeDateTimeValue = {
    type?: TimeframeFieldType;
    date?: Date;
    unit?: TimeframeFieldUnit;
    modifier?: TimeframeFieldModifier;
    modifierUnit?: TimeframeFieldUnit;
    modifierValue?: number;
};

const isTimeframeFieldUnit = (value: unknown): value is TimeframeFieldUnit => {
    return unitOptions.some((option) => option.value === value);
};

/**
 * Convert a custom timeframe value, into it's form UI equivalent.
 */
export function resolveCustomTimeframeFormValues(timeframe?: CustomTimeframeValue): RelativeDateTimeValue {
    // If no timeframe is provided, return UI for 'now'
    if (!timeframe || timeframe === 'now') {
        return {
            type: 'now',
            modifier: 'exact'
        };
    }

    // Handle unix timestamp conversion to fixed date
    if (isUnixEpochSeconds(timeframe)) {
        return {
            type: 'fixed',
            date: new Date(timeframe)
        };
    }

    // Attempt to parse as ISO date string, i.e. fixed date 2023-10-01T00:00:00Z
    if (isIsoDateString(timeframe)) {
        return {
            type: 'fixed',
            date: parseJSON(timeframe)
        };
    }

    // Attempt to parse form values from relative timeframe string, i.e. -1d@w+1d
    try {
        const { snappingType, snappingUnit, modifier, modifierUnit, modifierValue } =
            resolveTimeframeComponents(timeframe);

        if (snappingUnit && !isTimeframeFieldUnit(snappingUnit)) {
            throw new Error(`Invalid snapping unit ${snappingUnit}`);
        }

        if (modifierUnit && !isTimeframeFieldUnit(modifierUnit)) {
            throw new Error(`Invalid modifier unit ${modifierUnit}`);
        }

        return {
            type: snappingType,
            unit: snappingUnit,
            modifier,
            modifierValue,
            modifierUnit
        };
    } catch {
        // ignore
    }

    // Fallback UI for 'now', could be good to have a custom type, for manually entering relative timeframes
    return {
        type: 'now',
        unit: 'hour',
        modifier: 'exact'
    };
}

export const TIMEFRAME_MODIFIER_MAX_VALUE = 1000;
export const TIMEFRAME_MODIFIER_MIN_VALUE = 1;

export const timeframeFormFieldSchema = z
    .object({
        type: typeOptionsSchema,
        date: z.date().optional(),
        unit: unitOptionsSchema.optional(),
        modifier: modifierOptionsSchema.optional(),
        modifierUnit: unitOptionsSchema.optional(),
        modifierValue: z
            .number()
            .min(TIMEFRAME_MODIFIER_MIN_VALUE, {
                message: `Value must be greater than or equal to ${TIMEFRAME_MODIFIER_MIN_VALUE}.`
            })
            .max(TIMEFRAME_MODIFIER_MAX_VALUE, {
                message: `Value must be less than or equal to ${TIMEFRAME_MODIFIER_MAX_VALUE}.`
            })
            .optional()
    })
    .superRefine((field, ctx) => {
        const isExactTime = field.type === 'fixed' || field.modifier === 'exact';
        const isSnapping = field.type !== 'fixed' && field.type !== 'now';

        if (isSnapping && !field.unit) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Fill in all required fields.'
            });
            return;
        }

        const isValidModifier = field.modifierUnit && field.modifierValue && !isNaN(field.modifierValue);

        if ((!isExactTime && !isValidModifier) || (field.type === 'fixed' && !field.date)) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: 'Fill in all required fields.'
            });
        }
    });

export type TimeframeFormData = {
    from: RelativeDateTimeValue;
    to: RelativeDateTimeValue;
    weekStartsOn?: number; // 0 = Sunday, 1 = Monday, etc.
    yearStartsOn?: {
        day?: number; // 1-31
        month?: number; // 0 = January, 1 = February, etc.
    };
};

export const TimeframeFormSchema: ZodSchema<TimeframeFormData> = z
    .object({
        from: timeframeFormFieldSchema,
        to: timeframeFormFieldSchema,
        weekStartsOn: z.number().int().min(0).max(6).optional(),
        yearStartsOn: z
            .object({
                day: z.number().int().min(1).max(31).optional(),
                month: z.number().int().min(0).max(11).optional()
            })
            .optional()
    })
    .superRefine((data, ctx) => {
        const isValidFrom = timeframeFormFieldSchema.safeParse(data.from).success;
        const isValidTo = timeframeFormFieldSchema.safeParse(data.to).success;

        // skip surfacing extra errors as form not complete.
        if (!isValidFrom || !isValidTo) {
            return;
        }

        const options = {
            weekStartsOn: data.weekStartsOn as Day,
            yearStartsOn: data.yearStartsOn
        };

        const fromTimeframe = toCustomTimeframeValue(data.from);
        const toTimeframe = toCustomTimeframeValue(data.to);

        const fromDate = resolveTimeframeValue(fromTimeframe, options);
        const toDate = resolveTimeframeValue(toTimeframe, options);

        // don't want millisecond/second precision on checks, as we round to minute on server.
        fromDate.setMilliseconds(0);
        fromDate.setSeconds(0);
        toDate.setMilliseconds(0);
        toDate.setSeconds(0);

        const fromTimestamp = fromDate.getTime();
        const toTimestamp = toDate.getTime();

        if (fromTimestamp > toTimestamp) {
            ctx.addIssue({
                code: z.ZodIssueCode.invalid_date,
                message: "The 'From' date/time cannot be after the 'To' date/time.",
                path: ['from']
            });
        } else if (fromTimestamp === toTimestamp) {
            ctx.addIssue({
                code: z.ZodIssueCode.invalid_date,
                message: "The 'To' date/time cannot be the same as the 'From' date/time.",
                path: ['to']
            });
        }
    });
